import { useState, useEffect, useCallback } from 'react';
import { employeeAPI, socialAPI } from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  User, Money, CalendarBlank, Clock, Receipt, Laptop,
  SignOut, DownloadSimple, Plus, MapPin, Check, X,
  Warning, Lock, Trash, PencilSimple, Paperclip,
  Camera, FileText, MapPinLine, Briefcase, CaretRight,
  House, Cake, Star, Megaphone, Image as ImageIcon, Gift, Eye,
  TrendUp, CaretDown, UsersThree,
} from '@phosphor-icons/react';

const fmt = (v) => formatCurrency(v || 0);
const API_URL = process.env.REACT_APP_BACKEND_URL;

// ═══════════════════════════════════════════════════════════════════════════════
// EMPLOYEE LOGIN
// ═══════════════════════════════════════════════════════════════════════════════
function EmployeeLogin({ onLogin }) {
  const [mode, setMode] = useState('email'); // 'email' | 'id'
  const [empNumber, setEmpNumber] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = mode === 'email'
        ? await employeeAPI.emailLogin(email.trim(), password)
        : await employeeAPI.login(empNumber.toUpperCase(), password);
      localStorage.setItem('emp_token', res.data.token);
      localStorage.setItem('emp_data', JSON.stringify(res.data.employee));
      onLogin(res.data.employee, res.data.token);
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', background: '#0B1B3D' }} data-testid="emp-login-page">
      <div className="absolute inset-0 opacity-[0.12]" style={{ backgroundImage: 'linear-gradient(#2E7BC4 1px, transparent 1px), linear-gradient(90deg, #2E7BC4 1px, transparent 1px)', backgroundSize: '44px 44px' }} />
      <div className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-[#0F4C81]/40 blur-3xl" />
      <div className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-[#2E7BC4]/20 blur-3xl" />
      <div className="w-full max-w-sm relative z-10">
        <div className="text-center mb-8">
          <img src="/hearclear_logo.png" alt="HearClear" className="h-12 mx-auto mb-4 brightness-0 invert" data-testid="login-logo" />
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/10 px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-[#7FB2E5]">ESS Login</span>
          <p className="text-sm text-white/60 mt-3">Employee Self-Service Portal</p>
        </div>
        <form onSubmit={handleLogin} className="bg-white rounded-2xl shadow-2xl p-7 space-y-5">
          {error && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-xl px-4 py-2.5" data-testid="emp-login-error">{error}</div>}
          <div className="flex bg-slate-100 rounded-xl p-1">
            <button type="button" onClick={() => { setMode('email'); setError(''); }}
              className={`flex-1 text-sm py-1.5 rounded-lg font-medium transition-colors ${mode === 'email' ? 'bg-white text-[#0F4C81] shadow-sm' : 'text-slate-500'}`}
              data-testid="emp-login-mode-email">Work Email</button>
            <button type="button" onClick={() => { setMode('id'); setError(''); }}
              className={`flex-1 text-sm py-1.5 rounded-lg font-medium transition-colors ${mode === 'id' ? 'bg-white text-[#0F4C81] shadow-sm' : 'text-slate-500'}`}
              data-testid="emp-login-mode-id">Employee ID</button>
          </div>
          {mode === 'email' ? (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Work Email</label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@hearclear.in" className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-1 focus:ring-[#0F4C81] text-base" data-testid="emp-login-email" />
            </div>
          ) : (
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Employee ID</label>
              <Input value={empNumber} onChange={(e) => setEmpNumber(e.target.value)} placeholder="e.g. GOI0063" className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-1 focus:ring-[#0F4C81] text-base" data-testid="emp-login-number" />
            </div>
          )}
          <div>
            <label className="text-xs font-semibold text-slate-500 uppercase tracking-wider block mb-1.5">Password</label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password" className="h-12 rounded-xl border-slate-200 focus:border-[#0F4C81] focus:ring-1 focus:ring-[#0F4C81] text-base" data-testid="emp-login-password" />
          </div>
          <Button type="submit" disabled={loading || !password || (mode === 'email' ? !email : !empNumber)} className="w-full h-12 bg-[#0F4C81] hover:bg-[#0B3C68] rounded-xl text-base font-semibold" data-testid="emp-login-submit">
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
        <p className="text-center text-xs text-white/40 mt-8">© {new Date().getFullYear()} HearClear. Powered by Fintexa.</p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// PROFILE TAB (Photo, Documents, Address)
// ═══════════════════════════════════════════════════════════════════════════════
function IdentitySection() {
  const [identity, setIdentity] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [panVal, setPanVal] = useState('');
  const [aadhaarVal, setAadhaarVal] = useState('');

  useEffect(() => {
    employeeAPI.getIdentityStatus()
      .then(r => {
        setIdentity(r.data);
        setPanVal(r.data.pan_number || '');
        setAadhaarVal(r.data.aadhaar_number || '');
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!panVal && !aadhaarVal) { toast.error('Enter PAN or Aadhaar'); return; }
    setSaving(true);
    try {
      await employeeAPI.updateIdentity({ pan_number: panVal, aadhaar_number: aadhaarVal });
      toast.success('Identity documents saved and locked');
      setIdentity(prev => ({ ...prev, pan_number: panVal, aadhaar_number: aadhaarVal, locked: true }));
      setEditing(false);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  if (loading) return null;
  const locked = identity?.locked;

  return (
    <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 sm:p-6" data-testid="identity-section">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Lock size={18} className="text-[#0F4C81]" />
          <h3 className="font-semibold text-sm">Identity Documents</h3>
          {locked && <span className="text-[10px] bg-green-100 text-green-700 px-2 py-0.5 rounded-full font-medium">Verified & Locked</span>}
        </div>
        {!locked && !editing && (
          <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="edit-identity-btn">
            <PencilSimple size={14} className="mr-1" /> {identity?.pan_number ? 'Edit' : 'Add'}
          </Button>
        )}
      </div>

      {editing && !locked ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-[#64748B] block mb-1">PAN Number</label>
              <Input value={panVal} onChange={e => setPanVal(e.target.value.toUpperCase())} placeholder="ABCDE1234F" maxLength={10} className="font-mono" data-testid="identity-pan" />
            </div>
            <div>
              <label className="text-xs text-[#64748B] block mb-1">Aadhaar Number</label>
              <Input value={aadhaarVal} onChange={e => setAadhaarVal(e.target.value.replace(/\D/g, ''))} placeholder="1234 5678 9012" maxLength={12} className="font-mono" data-testid="identity-aadhaar" />
            </div>
          </div>
          <p className="text-[10px] text-amber-600">Once saved, these fields will be locked. Only admin can edit them.</p>
          <div className="flex gap-2">
            <Button size="sm" onClick={handleSave} disabled={saving} className="bg-[#0F4C81] text-white text-xs h-7" data-testid="save-identity-btn">{saving ? 'Saving...' : 'Save & Lock'}</Button>
            <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="text-xs h-7">Cancel</Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-[#F8FAFC] rounded-lg p-3">
            <p className="text-xs text-[#64748B]">PAN</p>
            <p className="font-medium text-sm text-[#0F4C81] font-mono">{identity?.pan_number || '—'}</p>
          </div>
          <div className="bg-[#F8FAFC] rounded-lg p-3">
            <p className="text-xs text-[#64748B]">Aadhaar</p>
            <p className="font-medium text-sm text-[#0F4C81] font-mono">{identity?.aadhaar_number || '—'}</p>
          </div>
        </div>
      )}
    </div>
  );
}

function ProfileTab({ profile, token, onProfileUpdate }) {
  const [photoUrl, setPhotoUrl] = useState(null);
  const [docs, setDocs] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [addressDialog, setAddressDialog] = useState(false);
  const [docDialog, setDocDialog] = useState(false);
  const [docType, setDocType] = useState('');
  const [docFile, setDocFile] = useState(null);
  const [docOtherName, setDocOtherName] = useState('');
  const formatAddress = (addr) => {
    if (!addr) return '';
    if (typeof addr === 'string') return addr;
    return [addr.line1, addr.line2, addr.city, addr.state, addr.zip].filter(Boolean).join(', ');
  };
  const [addrForm, setAddrForm] = useState({
    current_address: formatAddress(profile?.current_address),
    permanent_address: formatAddress(profile?.permanent_address),
  });

  useEffect(() => {
    // Try to load photo
    fetch(`${API_URL}/api/employee/me/photo`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => { if (r.ok) return r.blob(); throw new Error(); })
      .then(b => setPhotoUrl(URL.createObjectURL(b)))
      .catch(() => setPhotoUrl(null));
    // Load documents
    employeeAPI.getDocuments()
      .then(r => setDocs(r.data.documents || []))
      .catch(() => {});
  }, [token]);

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('photo', file);
      await fetch(`${API_URL}/api/employee/me/photo`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      }).then(r => { if (!r.ok) throw new Error(); });
      setPhotoUrl(URL.createObjectURL(file));
      toast.success('Photo uploaded');
      if (onProfileUpdate) onProfileUpdate();
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const handleDocUpload = async () => {
    if (!docType || !docFile) return;
    setUploading(true);
    try {
      const effectiveType = docType === 'Other' ? (docOtherName.trim() || 'Other') : docType;
      const fd = new FormData();
      fd.append('document_type', effectiveType);
      fd.append('document', docFile);
      await fetch(`${API_URL}/api/employee/me/documents`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
      }).then(r => { if (!r.ok) throw new Error(); return r.json(); });
      toast.success('Document uploaded');
      setDocDialog(false);
      setDocType(''); setDocFile(null); setDocOtherName('');
      employeeAPI.getDocuments().then(r => setDocs(r.data.documents || [])).catch(() => {});
    } catch { toast.error('Upload failed'); }
    finally { setUploading(false); }
  };

  const downloadDoc = async (docId, filename) => {
    try {
      const res = await fetch(`${API_URL}/api/employee/me/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const viewDoc = async (docId) => {
    try {
      const res = await fetch(`${API_URL}/api/employee/me/documents/${docId}/view`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch { toast.error('Unable to open document'); }
  };

  const handleSaveAddress = async () => {
    try {
      await employeeAPI.updateAddress(addrForm);
      toast.success('Address updated');
      setAddressDialog(false);
      if (onProfileUpdate) onProfileUpdate();
    } catch { toast.error('Failed to update'); }
  };

  if (!profile) return null;
  const fields = [
    ['Employee #', profile.employee_number], ['Department', profile.department],
    ['Designation', profile.job_title], ['Location', profile.location],
    ['Date of Joining', profile.date_joined], ['Reporting To', profile.reporting_to],
    ['Band / Grade', profile.band], ['Work Email', profile.work_email],
    ['Mobile', profile.mobile_phone], ['Gender', profile.gender],
    ['Date of Birth', profile.date_of_birth], ['PAN', profile.pan_number],
    ['PF Number', profile.pf_number], ['UAN', profile.uan_number],
  ];

  return (
    <div className="space-y-4" data-testid="emp-profile-tab">
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 sm:p-6">
        <div className="flex items-center gap-4 mb-4">
          <div className="relative group">
            {photoUrl ? (
              <img src={photoUrl} alt="Profile" className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-[#E2E8F0]" />
            ) : (
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#0F4C81] flex items-center justify-center text-white text-2xl font-bold">
                {profile.display_name?.charAt(0) || 'E'}
              </div>
            )}
            <label className="absolute bottom-0 right-0 bg-[#0F4C81] text-white rounded-full w-7 h-7 flex items-center justify-center cursor-pointer hover:bg-[#0B3C68] shadow-lg" data-testid="emp-photo-upload">
              <Camera size={14} />
              <input type="file" accept="image/*" className="hidden" onChange={handlePhotoUpload} disabled={uploading} />
            </label>
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-base sm:text-xl font-bold text-[#0F4C81] truncate">{profile.display_name}</h2>
            <p className="text-xs sm:text-sm text-[#64748B] truncate">{profile.job_title} — {profile.department}</p>
            <p className="text-xs text-[#64748B]">{profile.location}</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 sm:gap-3">
          {fields.map(([label, value]) => (
            <div key={label} className="bg-[#F8FAFC] rounded-lg p-3">
              <p className="text-xs text-[#64748B]">{label}</p>
              <p className="font-medium text-sm text-[#0F4C81] truncate">{value || '—'}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Address Section */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><MapPinLine size={18} className="text-[#0F4C81]" /><h3 className="font-semibold text-sm">Address Details</h3></div>
          <Button variant="outline" size="sm" onClick={() => { setAddrForm({ current_address: formatAddress(profile.current_address), permanent_address: formatAddress(profile.permanent_address) }); setAddressDialog(true); }} data-testid="emp-edit-address-btn">
            <PencilSimple size={14} className="mr-1" /> Edit
          </Button>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="bg-[#F8FAFC] rounded-lg p-3">
            <p className="text-xs text-[#64748B] mb-1">Current Address</p>
            <p className="text-sm text-[#0F4C81]">{typeof profile.current_address === 'object' ? [profile.current_address?.line1, profile.current_address?.line2, profile.current_address?.city, profile.current_address?.state, profile.current_address?.zip].filter(Boolean).join(', ') : (profile.current_address || '—')}</p>
          </div>
          <div className="bg-[#F8FAFC] rounded-lg p-3">
            <p className="text-xs text-[#64748B] mb-1">Permanent Address</p>
            <p className="text-sm text-[#0F4C81]">{typeof profile.permanent_address === 'object' ? [profile.permanent_address?.line1, profile.permanent_address?.line2, profile.permanent_address?.city, profile.permanent_address?.state, profile.permanent_address?.zip].filter(Boolean).join(', ') : (profile.permanent_address || '—')}</p>
          </div>
        </div>
      </div>

      {/* Identity Documents Section */}
      <IdentitySection />

      {/* Documents Section */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 sm:p-6">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2"><FileText size={18} className="text-[#0F4C81]" /><h3 className="font-semibold text-sm">My Documents</h3></div>
          <Button variant="outline" size="sm" onClick={() => setDocDialog(true)} data-testid="emp-upload-doc-btn"><Plus size={14} className="mr-1" /> Upload</Button>
        </div>
        {docs.length === 0 ? <p className="text-center py-4 text-sm text-[#64748B]">No documents uploaded</p> :
          <div className="space-y-2">
            {docs.map(d => (
              <div key={d.id} className="flex items-center justify-between bg-[#F8FAFC] rounded-lg p-3">
                <div className="flex items-center gap-2 min-w-0">
                  <FileText size={16} className="text-[#0F4C81] flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{d.filename}</p>
                    <p className="text-xs text-[#64748B]">{d.document_type}</p>
                  </div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <Button variant="ghost" size="sm" onClick={() => viewDoc(d.id)} className="text-blue-600" data-testid={`view-doc-${d.id}`}><Eye size={16} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => downloadDoc(d.id, d.filename)} data-testid={`download-doc-${d.id}`}><DownloadSimple size={16} /></Button>
                </div>
              </div>
            ))}
          </div>
        }
      </div>

      {/* Employee Journey */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-3"><Briefcase size={18} className="text-[#0F4C81]" /><h3 className="font-semibold text-sm">Employee Journey</h3></div>
        <div className="relative pl-6">
          <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-[#E2E8F0]" />
          {[
            { label: 'Joined', date: profile.date_joined, active: true },
            { label: 'Probation Confirmed', date: profile.confirmation_date || null, active: !!profile.confirmation_date },
            ...(profile.promotions || []).map(p => ({ label: `Promoted: ${p.title}`, date: p.date, active: true })),
            { label: profile.employment_status === 'active' ? 'Currently Active' : `Exit: ${profile.exit_date || ''}`, date: null, active: profile.employment_status === 'active' },
          ].map((step, i) => (
            <div key={i} className="relative mb-4 last:mb-0">
              <div className={`absolute -left-4 top-1 w-3 h-3 rounded-full border-2 ${step.active ? 'bg-[#0F4C81] border-[#0F4C81]' : 'bg-white border-[#E2E8F0]'}`} />
              <div className="ml-2">
                <p className={`text-sm font-medium ${step.active ? 'text-[#0F4C81]' : 'text-[#64748B]'}`}>{step.label}</p>
                {step.date && <p className="text-xs text-[#64748B]">{step.date}</p>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Address Dialog */}
      <Dialog open={addressDialog} onOpenChange={setAddressDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
          <DialogHeader><DialogTitle>Edit Address</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-[#64748B]">Current Address</label>
              <Textarea value={addrForm.current_address} onChange={(e) => setAddrForm(f => ({...f, current_address: e.target.value}))} rows={3} /></div>
            <div><label className="text-xs text-[#64748B]">Permanent Address</label>
              <Textarea value={addrForm.permanent_address} onChange={(e) => setAddrForm(f => ({...f, permanent_address: e.target.value}))} rows={3} /></div>
          </div>
          <DialogFooter><Button onClick={handleSaveAddress}>Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Document Upload Dialog */}
      <Dialog open={docDialog} onOpenChange={setDocDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
          <DialogHeader><DialogTitle>Upload Document</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger><SelectValue placeholder="Document type" /></SelectTrigger>
              <SelectContent>
                {['Aadhaar Card', 'PAN Card', 'Passport', 'Driving License', 'Bank Passbook', 'Education Certificate', 'Experience Letter', 'Other'].map(t => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {docType === 'Other' && (
              <Input placeholder="Enter document name" value={docOtherName} onChange={(e) => setDocOtherName(e.target.value)} data-testid="emp-doc-other-name" />
            )}
            <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setDocFile(e.target.files[0] || null)} />
            {docFile && <p className="text-xs text-green-600">{docFile.name} ({(docFile.size / 1024).toFixed(1)} KB)</p>}
          </div>
          <DialogFooter><Button onClick={handleDocUpload} disabled={!docType || !docFile || uploading || (docType === 'Other' && !docOtherName.trim())}>{uploading ? 'Uploading...' : 'Upload'}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MY SALARY TAB (sorted newest first)
// ═══════════════════════════════════════════════════════════════════════════════
function SalaryTab({ empNumber }) {
  const [payroll, setPayroll] = useState([]);
  const [ytd, setYtd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(null);

  useEffect(() => {
    employeeAPI.getPayroll()
      .then(res => { setPayroll(res.data.payroll || []); setYtd(res.data.ytd); })
      .catch(() => toast.error('Failed to load payroll'))
      .finally(() => setLoading(false));
  }, []);

  const handleDownload = async (month, idx) => {
    setDownloading(idx);
    try {
      await employeeAPI.downloadSalarySlip(empNumber, month);
      toast.success('Salary slip downloaded');
    } catch (err) {
      toast.error('Download failed — ' + (err.response?.status === 401 ? 'Session expired' : 'Try again'));
    } finally { setDownloading(null); }
  };

  const MON = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const parsePM = (pm) => {
    const s = String(pm || '');
    const y = (s.match(/(20\d{2})/) || [])[1];
    let m = -1;
    const mm = s.match(/([A-Za-z]{3,})/);
    if (mm) m = MON.findIndex(x => mm[1].toLowerCase().startsWith(x.toLowerCase()));
    if (m < 0) { const nm = s.match(/[-/](\d{1,2})/) || s.match(/(\d{1,2})[-/]/); if (nm) m = parseInt(nm[1], 10) - 1; }
    return { year: y ? parseInt(y, 10) : 0, m: m < 0 ? 0 : m };
  };
  // Indian financial year starts in April (month index 3)
  const fyStartYear = (year, m) => (m >= 3 ? year : year - 1);
  const fyLabel = (fy) => `FY ${fy}-${String((fy + 1) % 100).padStart(2, '0')}`;

  // Group by financial year, compute per-month running YTD net from the FY's earliest
  // month (= the joining month for a mid-year joiner, so YTD starts there — not April).
  const buildGroups = (rowsIn) => {
    const withKey = (rowsIn || []).map(p => {
      const { year, m } = parsePM(p.payroll_month);
      return { ...p, _sort: year * 12 + m, _fy: fyStartYear(year, m) };
    });
    const byFy = {};
    withKey.forEach(p => { (byFy[p._fy] = byFy[p._fy] || []).push(p); });
    return Object.keys(byFy).map(fy => {
      const asc = byFy[fy].slice().sort((a, b) => a._sort - b._sort);
      let cum = 0;
      asc.forEach(r => { cum += (r.total_net_pay || 0); r._ytd_net = cum; });
      return { fy: parseInt(fy, 10), rows: asc.reverse() };
    }).sort((a, b) => b.fy - a.fy);
  };

  if (loading) return <div className="text-center py-12 text-[#64748B]">Loading payroll...</div>;
  const groups = buildGroups(payroll);

  return (
    <div className="space-y-6" data-testid="emp-salary-tab">
      {ytd && (
        <div className="grid grid-cols-3 gap-2 sm:gap-4">
          <div className="bg-[#0F4C81] text-white rounded-xl p-3 sm:p-4"><p className="text-[10px] sm:text-xs opacity-70">YTD Gross</p><p className="text-lg sm:text-2xl font-bold truncate">{fmt(ytd.gross_ytd)}</p></div>
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4"><p className="text-[10px] sm:text-xs text-amber-600">YTD Deductions</p><p className="text-lg sm:text-2xl font-bold text-amber-700 truncate">{fmt(ytd.total_deductions_ytd || (ytd.pf_employee || 0) + (ytd.income_tax || 0))}</p></div>
          <div className="bg-green-50 border border-green-200 rounded-xl p-3 sm:p-4"><p className="text-[10px] sm:text-xs text-green-600">YTD Net Pay</p><p className="text-lg sm:text-2xl font-bold text-green-700 truncate">{fmt(ytd.net_pay_ytd)}</p></div>
        </div>
      )}

      {groups.length === 0 && <div className="text-center py-8 text-[#64748B] bg-white border border-[#E2E8F0] rounded-xl">No payroll records yet</div>}

      {groups.map(({ fy, rows }) => (
        <div key={fy} className="space-y-3" data-testid={`salary-fy-${fy}`}>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-[#0B1B3D]">{fyLabel(fy)}</span>
            <span className="text-[10px] text-[#64748B] bg-[#F1F5F9] px-2 py-0.5 rounded">{rows.length} month{rows.length !== 1 ? 's' : ''}</span>
          </div>

          {/* Mobile cards */}
          <div className="sm:hidden space-y-3">
            {rows.map((p, i) => {
              const dk = `${fy}-${i}`;
              return (
                <div key={i} className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid={`salary-card-${fy}-${i}`}>
                  <div className="flex justify-between items-start mb-3">
                    <div><p className="font-semibold text-[#0F4C81]">{p.payroll_month}</p><p className="text-xl font-bold text-[#0F4C81] mt-1">{fmt(p.total_net_pay)}</p><p className="text-[10px] text-[#64748B]">Net Pay · YTD {fmt(p._ytd_net)}</p></div>
                    <Button variant="outline" size="sm" onClick={() => handleDownload(p.payroll_month, dk)} disabled={downloading === dk} className="h-9 px-3 text-xs" data-testid={`emp-download-slip-${fy}-${i}`}>
                      <DownloadSimple size={14} className="mr-1" />{downloading === dk ? '...' : 'Slip'}
                    </Button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="bg-[#F8FAFC] rounded p-2"><p className="text-[#64748B]">Basic</p><p className="font-medium">{fmt(p.basic)}</p></div>
                    <div className="bg-[#F8FAFC] rounded p-2"><p className="text-[#64748B]">HRA</p><p className="font-medium">{fmt(p.hra)}</p></div>
                    <div className="bg-[#F8FAFC] rounded p-2"><p className="text-[#64748B]">Gross</p><p className="font-semibold">{fmt(p.gross)}</p></div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Desktop table */}
          <div className="hidden sm:block bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#F8FAFC]"><tr><th className="px-4 py-3 text-left">Month</th><th className="px-4 py-3 text-right">Basic</th><th className="px-4 py-3 text-right">HRA</th><th className="px-4 py-3 text-right">Gross</th><th className="px-4 py-3 text-right">PF</th><th className="px-4 py-3 text-right">Tax</th><th className="px-4 py-3 text-right">Net Pay</th><th className="px-4 py-3 text-right">YTD Net</th><th className="px-4 py-3 text-center">Slip</th></tr></thead>
              <tbody>
                {rows.map((p, i) => {
                  const dk = `${fy}-${i}`;
                  return (
                    <tr key={i} className="border-t border-[#E2E8F0]">
                      <td className="px-4 py-3 font-medium">{p.payroll_month}</td>
                      <td className="px-4 py-3 text-right">{fmt(p.basic)}</td><td className="px-4 py-3 text-right">{fmt(p.hra)}</td>
                      <td className="px-4 py-3 text-right font-semibold">{fmt(p.gross)}</td>
                      <td className="px-4 py-3 text-right text-amber-600">{fmt(p.pf_employee)}</td>
                      <td className="px-4 py-3 text-right text-red-600">{fmt(p.income_tax)}</td>
                      <td className="px-4 py-3 text-right font-bold text-[#0F4C81]">{fmt(p.total_net_pay)}</td>
                      <td className="px-4 py-3 text-right text-[#0B1B3D] font-medium">{fmt(p._ytd_net)}</td>
                      <td className="px-4 py-3 text-center"><Button variant="ghost" size="sm" onClick={() => handleDownload(p.payroll_month, dk)} disabled={downloading === dk} className="h-8 w-8 p-0"><DownloadSimple size={16} className="text-[#0F4C81]" /></Button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MY LEAVE TAB (Cancel + Attachment + Comp-off)
// ═══════════════════════════════════════════════════════════════════════════════
function LeaveTab() {
  const [balances, setBalances] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyDialog, setApplyDialog] = useState(false);
  const [compoffDialog, setCompoffDialog] = useState(false);
  const [compoffs, setCompoffs] = useState([]);
  const [form, setForm] = useState({ leave_type_code: '', start_date: '', end_date: '', reason: '', half_day: false });
  const [leaveFile, setLeaveFile] = useState(null);
  const [compoffForm, setCompoffForm] = useState({ worked_date: '', reason: '', hours_worked: 8 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lRes, cRes] = await Promise.all([employeeAPI.getLeaves(), employeeAPI.getCompoffs()]);
      setBalances(lRes.data.balances || []);
      setRequests(lRes.data.requests || []);
      setCompoffs(cRes.data.requests || []);
    } catch { toast.error('Failed to load leave data'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleApply = async () => {
    try {
      if (leaveFile) {
        const fd = new FormData();
        fd.append('leave_type_code', form.leave_type_code);
        fd.append('start_date', form.start_date);
        fd.append('end_date', form.end_date);
        fd.append('reason', form.reason);
        fd.append('half_day', form.half_day);
        fd.append('attachment', leaveFile);
        const token = localStorage.getItem('emp_token');
        await fetch(`${API_URL}/api/employee/my-leaves/apply-with-attachment`, {
          method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd,
        }).then(r => { if (!r.ok) throw new Error('Failed'); });
      } else {
        await employeeAPI.applyLeave(form);
      }
      toast.success('Leave applied');
      setApplyDialog(false);
      setForm({ leave_type_code: '', start_date: '', end_date: '', reason: '', half_day: false });
      setLeaveFile(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || 'Failed'); }
  };

  const handleCancel = async (leaveId) => {
    try {
      await employeeAPI.cancelLeave(leaveId);
      toast.success('Leave cancelled');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const handleCompoff = async () => {
    try {
      await employeeAPI.requestCompoff(compoffForm);
      toast.success('Comp-off requested');
      setCompoffDialog(false);
      setCompoffForm({ worked_date: '', reason: '', hours_worked: 8 });
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const downloadAttachment = async (leaveId, filename) => {
    try {
      const token = localStorage.getItem('emp_token');
      const res = await fetch(`${API_URL}/api/employee/my-leaves/${leaveId}/attachment`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  if (loading) return <div className="text-center py-12 text-[#64748B]">Loading...</div>;

  return (
    <div className="space-y-4" data-testid="emp-leave-tab">
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 sm:gap-3">
        {balances.filter(b => b.leave_type_code !== 'LWP').map(b => (
          <div key={b.leave_type_code} className="bg-white border border-[#E2E8F0] rounded-xl p-3">
            <span className="font-semibold text-xs truncate block">{b.leave_type_name || b.leave_type_code}</span>
            <div className="text-2xl font-bold text-[#0F4C81]">{b.available}</div>
            <p className="text-[10px] text-[#64748B]">Used: {b.used} | Total: {b.annual_quota}</p>
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" size="sm" onClick={() => setCompoffDialog(true)} data-testid="emp-compoff-btn"><Plus size={14} className="mr-1" /> Comp-Off</Button>
        <Button size="sm" onClick={() => setApplyDialog(true)} data-testid="emp-apply-leave-btn"><Plus size={14} className="mr-1" /> Apply Leave</Button>
      </div>

      {/* Leave Requests */}
      <div className="sm:hidden space-y-2">
        {requests.length === 0 ? <div className="text-center py-8 text-[#64748B] bg-white border border-[#E2E8F0] rounded-xl text-sm">No leave requests</div> :
          requests.map(r => (
            <div key={r.id} className="bg-white border border-[#E2E8F0] rounded-xl p-3">
              <div className="flex justify-between items-start">
                <div>
                  <span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{r.leave_type_code}</span>
                  <p className="text-sm font-medium mt-1">{r.start_date} — {r.end_date}</p>
                  <p className="text-xs text-[#64748B]">{r.days} day(s) | {r.reason || 'No reason'}</p>
                  {r.has_attachment && <button onClick={() => downloadAttachment(r.id, r.attachment_filename)} className="text-xs text-blue-600 underline mt-0.5">Attachment</button>}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.status === 'approved' ? 'bg-green-100 text-green-700' : r.status === 'cancelled' ? 'bg-gray-100 text-gray-600' : r.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span>
                  {(r.status === 'pending' || r.status === 'approved') && (
                    <button onClick={() => handleCancel(r.id)} className="text-xs text-red-500 underline" data-testid={`cancel-leave-${r.id}`}>Cancel</button>
                  )}
                </div>
              </div>
            </div>
          ))
        }
      </div>
      <div className="hidden sm:block bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC]"><tr><th className="px-4 py-3 text-left">Type</th><th className="px-4 py-3 text-left">From</th><th className="px-4 py-3 text-left">To</th><th className="px-4 py-3 text-right">Days</th><th className="px-4 py-3 text-left">Reason</th><th className="px-4 py-3 text-center">Attach</th><th className="px-4 py-3 text-center">Status</th><th className="px-4 py-3 text-center">Action</th></tr></thead>
          <tbody>
            {requests.length === 0 ? <tr><td colSpan={8} className="text-center py-8 text-[#64748B]">No leave requests</td></tr> :
              requests.map(r => (
                <tr key={r.id} className="border-t border-[#E2E8F0]">
                  <td className="px-4 py-3"><span className="px-2 py-0.5 bg-blue-50 text-blue-700 rounded text-xs font-medium">{r.leave_type_code}</span></td>
                  <td className="px-4 py-3">{r.start_date}</td><td className="px-4 py-3">{r.end_date}</td>
                  <td className="px-4 py-3 text-right">{r.days}{r.lop_days > 0 && <span className="block text-[10px] text-amber-600 font-medium">LOP {r.lop_days}d</span>}</td>
                  <td className="px-4 py-3 text-[#64748B]">{r.reason || '—'}</td>
                  <td className="px-4 py-3 text-center">{r.has_attachment ? <button onClick={() => downloadAttachment(r.id, r.attachment_filename)} className="text-blue-600"><Paperclip size={14} /></button> : '—'}</td>
                  <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded text-xs font-medium ${r.status === 'approved' ? 'bg-green-100 text-green-700' : r.status === 'cancelled' ? 'bg-gray-100 text-gray-600' : r.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span>{r.status === 'pending' && r.approval_stage === 'manager' && <span className="block text-[10px] text-[#64748B] mt-0.5">Awaiting manager</span>}{r.status === 'pending' && r.approval_stage === 'hr' && <span className="block text-[10px] text-[#64748B] mt-0.5">Awaiting HR</span>}</td>
                  <td className="px-4 py-3 text-center">{(r.status === 'pending' || r.status === 'approved') && <Button variant="ghost" size="sm" onClick={() => handleCancel(r.id)} className="text-red-500 h-7 text-xs" data-testid={`cancel-leave-${r.id}`}><X size={12} className="mr-1" />Cancel</Button>}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      {/* Comp-Off Requests */}
      {compoffs.length > 0 && (
        <div>
          <h3 className="font-semibold text-sm text-[#0F4C81] mb-2">Comp-Off Requests</h3>
          <div className="space-y-2">
            {compoffs.map(c => (
              <div key={c.id} className="bg-white border border-[#E2E8F0] rounded-xl p-3 flex justify-between items-center">
                <div><p className="text-sm font-medium">Worked: {c.worked_date}</p><p className="text-xs text-[#64748B]">{c.hours_worked}h | {c.reason || '—'}</p></div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.status === 'approved' ? 'bg-green-100 text-green-700' : c.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{c.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Apply Leave Dialog */}
      <Dialog open={applyDialog} onOpenChange={setApplyDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
          <DialogHeader><DialogTitle>Apply Leave</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Select value={form.leave_type_code} onValueChange={(v) => setForm(f => ({...f, leave_type_code: v}))}>
              <SelectTrigger><SelectValue placeholder="Leave type" /></SelectTrigger>
              <SelectContent>{balances.map(b => <SelectItem key={b.leave_type_code} value={b.leave_type_code}>{b.leave_type_name || b.leave_type_code} ({b.available})</SelectItem>)}</SelectContent>
            </Select>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#64748B]">From</label><Input type="date" value={form.start_date} onChange={(e) => setForm(f => ({...f, start_date: e.target.value}))} /></div>
              <div><label className="text-xs text-[#64748B]">To</label><Input type="date" value={form.end_date} onChange={(e) => setForm(f => ({...f, end_date: e.target.value}))} /></div>
            </div>
            <Input placeholder="Reason for leave" value={form.reason} onChange={(e) => setForm(f => ({...f, reason: e.target.value}))} />
            <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={form.half_day} onChange={(e) => setForm(f => ({...f, half_day: e.target.checked}))} /> Half day</label>
            <div><label className="text-xs text-[#64748B] block mb-1">Attachment (optional)</label>
              <Input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => setLeaveFile(e.target.files[0] || null)} data-testid="leave-attachment" className="text-sm" />
              {leaveFile && <p className="text-xs text-green-600 mt-1">{leaveFile.name}</p>}
            </div>
            {(() => {
              const bal = balances.find(b => b.leave_type_code === form.leave_type_code);
              let days = 0;
              if (form.start_date && form.end_date) {
                const s = new Date(form.start_date), e = new Date(form.end_date);
                days = Math.max(0, Math.round((e - s) / 86400000) + 1);
                if (form.half_day) days = 0.5;
              }
              const avail = bal ? (bal.available || 0) : 0;
              const lop = Math.max(0, days - avail);
              if (days > 0 && lop > 0) {
                return <div className="text-xs bg-amber-50 border border-amber-200 text-amber-700 rounded-lg p-2" data-testid="leave-lop-warning">Balance is {avail} day(s). <b>{lop} day(s) will be Loss of Pay (LOP)</b> and deducted from your salary. You can still submit.</div>;
              }
              return null;
            })()}
          </div>
          <DialogFooter><Button onClick={handleApply} disabled={!form.leave_type_code || !form.start_date || !form.end_date} data-testid="emp-submit-leave-btn">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Comp-Off Dialog */}
      <Dialog open={compoffDialog} onOpenChange={setCompoffDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
          <DialogHeader><DialogTitle>Request Comp-Off</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-[#64748B]">Date Worked</label><Input type="date" value={compoffForm.worked_date} onChange={(e) => setCompoffForm(f => ({...f, worked_date: e.target.value}))} /></div>
            <div><label className="text-xs text-[#64748B]">Hours Worked</label><Input type="number" value={compoffForm.hours_worked} onChange={(e) => setCompoffForm(f => ({...f, hours_worked: parseInt(e.target.value) || 0}))} /></div>
            <Input placeholder="Reason (e.g., Weekend deployment)" value={compoffForm.reason} onChange={(e) => setCompoffForm(f => ({...f, reason: e.target.value}))} />
          </div>
          <DialogFooter><Button onClick={handleCompoff} disabled={!compoffForm.worked_date}>Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ATTENDANCE TAB
// ═══════════════════════════════════════════════════════════════════════════════
function AttendanceTab() {
  const [records, setRecords] = useState([]);
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [geoStatus, setGeoStatus] = useState('');
  const [geoError, setGeoError] = useState('');
  const [hasActiveCheckin, setHasActiveCheckin] = useState(false);
  const [todayRecord, setTodayRecord] = useState(null);
  const [regDialog, setRegDialog] = useState(false);
  const [regForm, setRegForm] = useState({ date: '', check_in_time: '09:30', check_out_time: '18:30', reason: '', clinic_name: '' });
  const [submittingReg, setSubmittingReg] = useState(false);
  const [regularizations, setRegularizations] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeeAPI.getAttendance(selectedMonth);
      const recs = res.data.records || [];
      setRecords(recs);
      setSummary(res.data.summary || {});
      const todayStr = new Date().toISOString().split('T')[0];
      const active = recs.find(r => r.date === todayStr && !r.check_out_time);
      setHasActiveCheckin(!!active);
      setTodayRecord(recs.find(r => r.date === todayStr) || null);
    } catch { toast.error('Failed to load attendance'); }
    finally { setLoading(false); }
  }, [selectedMonth]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    employeeAPI.getRegularizations().then(res => setRegularizations(res.data.regularizations || [])).catch(() => {});
  }, []);

  const handleRegularize = async () => {
    if (!regForm.date || !regForm.check_in_time || !regForm.check_out_time || !regForm.reason) {
      toast.error('All fields are required'); return;
    }
    setSubmittingReg(true);
    try {
      await employeeAPI.requestRegularization(regForm);
      toast.success('Regularization request submitted');
      setRegDialog(false);
      setRegForm({ date: '', check_in_time: '09:30', check_out_time: '18:30', reason: '', clinic_name: '' });
      const res = await employeeAPI.getRegularizations();
      setRegularizations(res.data.regularizations || []);
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to submit'); }
    finally { setSubmittingReg(false); }
  };

  const getLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('Geolocation not supported')); return; }
    setGeoStatus('Getting your location...'); setGeoError('');
    const timeout = setTimeout(() => { setGeoStatus(''); setGeoError('Location timed out'); reject(new Error('Timeout')); }, 20000);
    navigator.geolocation.getCurrentPosition(
      pos => { clearTimeout(timeout); setGeoStatus(''); resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }); },
      err => { clearTimeout(timeout); setGeoStatus(''); const msg = err.code === 1 ? 'Location denied' : err.code === 2 ? 'Location unavailable' : 'Location timed out'; setGeoError(msg); reject(new Error(msg)); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  });

  const handleCheckIn = async () => {
    setCheckingIn(true); setGeoError('');
    try {
      const loc = await getLocation();
      setGeoStatus('Sending check-in...');
      const res = await employeeAPI.checkIn(loc);
      if (res.data.is_remote) {
        toast.warning(res.data.message || 'Checked in remotely');
      } else {
        toast.success(res.data.message || 'Checked in');
      }
      setGeoStatus(''); await load();
    } catch (e) {
      const errMsg = e.response?.data?.detail || e.message || 'Check-in failed';
      toast.error(errMsg);
      setGeoError(errMsg);
      setGeoStatus('');
    }
    finally { setCheckingIn(false); }
  };

  const handleCheckOut = async () => {
    setCheckingOut(true); setGeoError('');
    try {
      const loc = await getLocation();
      setGeoStatus('Sending check-out...');
      const res = await employeeAPI.checkOut(loc);
      toast.success(`Checked out. ${res.data.total_hours || 0}h today.`);
      setGeoStatus(''); await load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); setGeoStatus(''); }
    finally { setCheckingOut(false); }
  };

  return (
    <div className="space-y-4" data-testid="emp-attendance-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-3 sm:p-4"><p className="text-[10px] sm:text-xs text-[#64748B]">Days Present</p><p className="text-xl sm:text-2xl font-bold">{summary.total_days || 0}</p></div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-3 sm:p-4"><p className="text-[10px] sm:text-xs text-[#64748B]">Late Arrivals</p><p className="text-xl sm:text-2xl font-bold text-red-600">{summary.late_count || 0}</p></div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-3 sm:p-4"><p className="text-[10px] sm:text-xs text-[#64748B]">Total Hours</p><p className="text-xl sm:text-2xl font-bold text-[#0F4C81]">{summary.total_hours || 0}</p></div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-3 sm:p-4"><p className="text-[10px] sm:text-xs text-[#64748B]">Avg Hrs/Day</p><p className="text-xl sm:text-2xl font-bold">{summary.avg_hours || 0}</p></div>
      </div>
      <div className="bg-white border-2 border-[#E2E8F0] rounded-xl p-4 sm:p-6">
        <h3 className="font-semibold text-base text-[#0F4C81] mb-1">Today's Attendance</h3>
        <p className="text-xs text-[#64748B] mb-3">{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</p>
        {todayRecord && (
          <div className="bg-[#F8FAFC] rounded-lg p-3 mb-3 text-sm">
            <div className="flex items-center gap-2 text-[#0F4C81]"><Check size={16} className="text-green-600" weight="bold" /><span className="font-medium">In: {todayRecord.check_in_time}</span>{todayRecord.is_remote && <span className="text-[10px] bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded font-medium">REMOTE</span>}{todayRecord.clinic_name && <span className="text-xs text-[#64748B]">— {todayRecord.clinic_name}</span>}</div>
            {todayRecord.check_out_time && <div className="flex items-center gap-2 text-[#0F4C81] mt-1"><SignOut size={16} className="text-amber-600" /><span className="font-medium">Out: {todayRecord.check_out_time}</span><span className="text-xs text-[#64748B]">— {todayRecord.total_hours}h</span></div>}
            {todayRecord.is_late && <p className="text-xs text-red-600 mt-1"><Warning size={12} className="inline mr-1" />Late by {todayRecord.late_minutes} min</p>}
          </div>
        )}
        {geoStatus && <p className="text-xs text-blue-600 mb-2"><MapPin size={12} className="inline animate-pulse mr-1" />{geoStatus}</p>}
        {geoError && <p className="text-xs text-red-600 mb-2"><Warning size={12} className="inline mr-1" />{geoError}</p>}
        <div className="flex flex-col sm:flex-row gap-2">
          {!hasActiveCheckin ? (
            <Button onClick={handleCheckIn} disabled={checkingIn || (todayRecord && todayRecord.check_out_time)} className="w-full sm:w-auto h-12 px-6 bg-green-600 hover:bg-green-700 text-base" data-testid="emp-checkin-btn">
              <Clock size={18} className="mr-2" />{checkingIn ? 'Checking in...' : (todayRecord?.check_out_time) ? 'Completed' : 'Check In'}
            </Button>
          ) : (
            <Button onClick={handleCheckOut} disabled={checkingOut} className="w-full sm:w-auto h-12 px-6 bg-amber-600 hover:bg-amber-700 text-base" data-testid="emp-checkout-btn">
              <SignOut size={18} className="mr-2" />{checkingOut ? 'Checking out...' : 'Check Out'}
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between gap-2 bg-white border border-[#E2E8F0] rounded-xl p-3">
        <h3 className="font-semibold text-sm text-[#0F4C81]">Attendance History</h3>
        <input type="month" value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value)} max={new Date().toISOString().slice(0, 7)} className="border border-[#E2E8F0] rounded-md px-2 py-1.5 text-sm" data-testid="emp-attendance-month" />
      </div>
      <div className="sm:hidden space-y-2">
        <h3 className="font-semibold text-sm text-[#0F4C81]">Records</h3>
        {records.length === 0 ? <div className="text-center py-8 text-[#64748B] text-sm bg-white border border-[#E2E8F0] rounded-xl">No records</div> :
          records.map(r => (
            <div key={r.id || r.date} className="bg-white border border-[#E2E8F0] rounded-xl p-3">
              <div className="flex justify-between items-start"><div><p className="font-medium text-sm">{r.date}</p><p className="text-xs text-[#64748B]">{r.check_in_time} — {r.check_out_time || 'Active'}</p></div>
                <div className="text-right">{r.total_hours && <p className="font-semibold text-sm">{r.total_hours}h</p>}{r.is_late && <span className="text-xs text-red-500">Late</span>}</div>
              </div>
            </div>
          ))
        }
      </div>
      <div className="hidden sm:block bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC]"><tr><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-left">Check In</th><th className="px-4 py-3 text-left">Check Out</th><th className="px-4 py-3 text-right">Hours</th><th className="px-4 py-3 text-left">Clinic</th><th className="px-4 py-3 text-center">Status</th></tr></thead>
          <tbody>
            {records.map(r => (
              <tr key={r.id || r.date} className="border-t border-[#E2E8F0]"><td className="px-4 py-3">{r.date}</td><td className="px-4 py-3">{r.check_in_time}</td><td className="px-4 py-3">{r.check_out_time || '—'}</td><td className="px-4 py-3 text-right font-medium">{r.total_hours || '—'}</td><td className="px-4 py-3 text-xs text-[#64748B]">{r.clinic_name || '—'}</td>
                <td className="px-4 py-3 text-center">{r.is_late ? <span className="text-xs text-red-500 bg-red-50 px-2 py-0.5 rounded">Late ({r.late_minutes}m)</span> : <span className="text-xs text-green-600">On time</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Regularization Section */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-semibold text-sm text-[#0F4C81]">Attendance Regularization</h3>
          <Button size="sm" onClick={() => setRegDialog(true)} className="h-8 bg-[#0B1B3D] hover:bg-[#16274D] text-xs" data-testid="request-regularization-btn">
            <Plus size={14} className="mr-1" /> Request Regularization
          </Button>
        </div>
        {regularizations.length === 0 ? (
          <p className="text-xs text-[#64748B] text-center py-4">No regularization requests yet</p>
        ) : (
          <div className="space-y-2">
            {regularizations.map(r => (
              <div key={r.id} className="border border-[#E2E8F0] rounded-lg p-3 flex justify-between items-center" data-testid={`reg-item-${r.id}`}>
                <div>
                  <p className="text-sm font-medium text-[#0F4C81]">{r.date}</p>
                  <p className="text-xs text-[#64748B]">{r.check_in_time} — {r.check_out_time} | {r.total_hours}h | {r.reason}</p>
                </div>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${r.status === 'approved' ? 'bg-green-100 text-green-700' : r.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{r.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Regularization Dialog */}
      <Dialog open={regDialog} onOpenChange={setRegDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md" data-testid="regularization-dialog">
          <DialogHeader><DialogTitle>Request Attendance Regularization</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-[#64748B]">Date</label>
              <Input type="date" value={regForm.date} onChange={e => setRegForm(f => ({...f, date: e.target.value}))} data-testid="reg-date" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-[#64748B]">Check-in Time</label>
                <Input type="time" value={regForm.check_in_time} onChange={e => setRegForm(f => ({...f, check_in_time: e.target.value}))} data-testid="reg-checkin" /></div>
              <div><label className="text-xs text-[#64748B]">Check-out Time</label>
                <Input type="time" value={regForm.check_out_time} onChange={e => setRegForm(f => ({...f, check_out_time: e.target.value}))} data-testid="reg-checkout" /></div>
            </div>
            <div><label className="text-xs text-[#64748B]">Reason</label>
              <Input placeholder="e.g. Forgot to punch in, System error" value={regForm.reason} onChange={e => setRegForm(f => ({...f, reason: e.target.value}))} data-testid="reg-reason" /></div>
            <div><label className="text-xs text-[#64748B]">Clinic / Location</label>
              <Input placeholder="Optional" value={regForm.clinic_name} onChange={e => setRegForm(f => ({...f, clinic_name: e.target.value}))} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRegDialog(false)}>Cancel</Button>
            <Button onClick={handleRegularize} disabled={submittingReg || !regForm.date || !regForm.reason} data-testid="submit-regularization-btn">
              {submittingReg ? 'Submitting...' : 'Submit Request'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// REIMBURSEMENT TAB (Edit/Delete + Vehicle fixed rates)
// ═══════════════════════════════════════════════════════════════════════════════
function ReimbursementTab() {
  const [claims, setClaims] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialog, setAddDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(false);
  const [editClaim, setEditClaim] = useState(null);
  const [form, setForm] = useState({ category: '', amount: '', description: '', receipt_date: '', vendor_name: '', km: '' });
  const [receiptFile, setReceiptFile] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await employeeAPI.getReimbursements();
      setClaims(res.data.claims || []);
      setCategories(res.data.categories || []);
    } catch { toast.error('Failed to load'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectedCat = categories.find(c => c.code === form.category);
  const isVehicle = selectedCat?.fixed_rate > 0;

  useEffect(() => {
    if (isVehicle && form.km) {
      setForm(f => ({...f, amount: String((parseFloat(form.km) || 0) * selectedCat.fixed_rate)}));
    }
  }, [form.km, form.category, isVehicle, selectedCat]);

  const submitClaim = async (isEdit = false) => {
    if (!form.category) { toast.error('Please select a category'); return; }
    if (!form.amount || parseFloat(form.amount) <= 0) { toast.error('Please enter a valid amount'); return; }
    if (!isEdit && !receiptFile) { toast.error('A receipt / reference document is mandatory'); return; }
    try {
      const fd = new FormData();
      fd.append('category', form.category);
      fd.append('amount', parseFloat(form.amount));
      fd.append('description', form.description + (isVehicle && form.km ? ` [${form.km} km @ ${selectedCat.fixed_rate}/km]` : ''));
      fd.append('receipt_date', form.receipt_date);
      fd.append('vendor_name', form.vendor_name);
      if (receiptFile) fd.append('receipt_file', receiptFile);
      const token = localStorage.getItem('emp_token');
      const url = isEdit ? `${API_URL}/api/employee/my-reimbursements/${editClaim.id}` : `${API_URL}/api/employee/my-reimbursements`;
      await fetch(url, {
        method: isEdit ? 'PUT' : 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      }).then(r => { if (!r.ok) throw new Error('Failed'); });
      toast.success(isEdit ? 'Claim updated' : 'Claim submitted');
      setAddDialog(false); setEditDialog(false);
      setForm({ category: '', amount: '', description: '', receipt_date: '', vendor_name: '', km: '' });
      setReceiptFile(null); setEditClaim(null);
      load();
    } catch (e) { toast.error(e.message || 'Failed'); }
  };

  const handleDelete = async (claimId) => {
    try {
      await employeeAPI.deleteReimbursement(claimId);
      toast.success('Claim deleted');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const openEdit = (c) => {
    setEditClaim(c);
    setForm({ category: c.category, amount: String(c.amount), description: c.description || '', receipt_date: c.receipt_date || '', vendor_name: c.vendor_name || '', km: '' });
    setReceiptFile(null);
    setEditDialog(true);
  };

  const downloadReceipt = async (claimId, filename) => {
    try {
      const token = localStorage.getItem('emp_token');
      const res = await fetch(`${API_URL}/api/employee/reimbursement/${claimId}/receipt`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  const totalPending = claims.filter(c => c.status === 'pending').reduce((s, c) => s + c.amount, 0);
  const totalApproved = claims.filter(c => c.status === 'approved' || c.status === 'paid').reduce((s, c) => s + c.amount, 0);

  const claimForm = (isEdit) => (
    <div className="space-y-3">
      <Select value={form.category} onValueChange={(v) => setForm(f => ({...f, category: v, km: '', amount: ''}))}>
        <SelectTrigger><SelectValue placeholder="Category" /></SelectTrigger>
        <SelectContent>{categories.map(c => <SelectItem key={c.code} value={c.code}>{c.name}{c.fixed_rate ? ` (${c.fixed_rate}/km)` : ''}</SelectItem>)}</SelectContent>
      </Select>
      {isVehicle && (
        <div><label className="text-xs text-[#64748B]">Distance (km)</label>
          <Input type="number" placeholder="Enter km" value={form.km} onChange={(e) => setForm(f => ({...f, km: e.target.value}))} />
          {form.km && <p className="text-xs text-green-600 mt-1">{form.km} km x {selectedCat.fixed_rate}/km = {fmt(parseFloat(form.amount))}</p>}
        </div>
      )}
      <Input type="number" placeholder="Amount (INR)" value={form.amount} onChange={(e) => setForm(f => ({...f, amount: e.target.value}))} disabled={isVehicle} />
      <Input placeholder="Description" value={form.description} onChange={(e) => setForm(f => ({...f, description: e.target.value}))} />
      <div className="grid grid-cols-2 gap-3">
        <div><label className="text-xs text-[#64748B]">Receipt Date</label><Input type="date" value={form.receipt_date} onChange={(e) => setForm(f => ({...f, receipt_date: e.target.value}))} /></div>
        <div><label className="text-xs text-[#64748B]">Vendor</label><Input placeholder="Vendor" value={form.vendor_name} onChange={(e) => setForm(f => ({...f, vendor_name: e.target.value}))} /></div>
      </div>
      <div><label className="text-xs text-[#64748B] block mb-1">Invoice / Receipt {!isEdit && <span className="text-red-500">* (required)</span>}</label>
        <Input type="file" accept=".pdf,.jpg,.jpeg,.png,.webp" onChange={(e) => setReceiptFile(e.target.files[0] || null)} className="text-sm" data-testid="emp-claim-receipt" />
        {receiptFile && <p className="text-xs text-green-600 mt-1">{receiptFile.name}</p>}
        {!isEdit && !receiptFile && <p className="text-xs text-amber-600 mt-1">Attach the bill/invoice to submit this claim.</p>}
      </div>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="emp-reimbursement-tab">
      <div className="grid grid-cols-3 gap-2 sm:gap-4">
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 sm:p-4"><p className="text-[10px] sm:text-xs text-amber-600">Pending</p><p className="text-lg sm:text-2xl font-bold text-amber-700 truncate">{fmt(totalPending)}</p></div>
        <div className="bg-green-50 border border-green-200 rounded-xl p-3 sm:p-4"><p className="text-[10px] sm:text-xs text-green-600">Approved</p><p className="text-lg sm:text-2xl font-bold text-green-700 truncate">{fmt(totalApproved)}</p></div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-3 sm:p-4"><p className="text-[10px] sm:text-xs text-[#64748B]">Total Claims</p><p className="text-lg sm:text-2xl font-bold">{claims.length}</p></div>
      </div>
      <div className="flex justify-end"><Button onClick={() => { setForm({ category: '', amount: '', description: '', receipt_date: '', vendor_name: '', km: '' }); setReceiptFile(null); setAddDialog(true); }} size="sm" data-testid="emp-new-claim-btn"><Plus size={14} className="mr-1" /> New Claim</Button></div>

      <div className="sm:hidden space-y-2">
        {claims.length === 0 ? <div className="text-center py-8 text-[#64748B] bg-white border border-[#E2E8F0] rounded-xl text-sm">No claims</div> :
          claims.map(c => (
            <div key={c.id} className="bg-white border border-[#E2E8F0] rounded-xl p-3">
              <div className="flex justify-between items-start">
                <div>
                  <span className="px-2 py-0.5 bg-[#F8FAFC] rounded text-xs">{c.category}</span>
                  <p className="font-bold text-[#0F4C81] mt-1">{fmt(c.amount)}</p>
                  <p className="text-xs text-[#64748B]">{c.description || '—'}</p>
                  {c.has_receipt && <button onClick={() => downloadReceipt(c.id, c.receipt_filename)} className="text-xs text-blue-600 underline mt-0.5">{c.receipt_filename}</button>}
                </div>
                <div className="flex flex-col items-end gap-1 flex-shrink-0">
                  <span className={`px-2 py-0.5 rounded text-xs font-medium ${c.status === 'approved' ? 'bg-green-100 text-green-700' : c.status === 'paid' ? 'bg-blue-100 text-blue-700' : c.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{c.status}</span>
                  {c.status === 'pending' && (
                    <div className="flex gap-1">
                      <button onClick={() => openEdit(c)} className="text-xs text-blue-600"><PencilSimple size={14} /></button>
                      <button onClick={() => handleDelete(c.id)} className="text-xs text-red-500"><Trash size={14} /></button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))
        }
      </div>
      <div className="hidden sm:block bg-white border border-[#E2E8F0] rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC]"><tr><th className="px-4 py-3 text-left">Category</th><th className="px-4 py-3 text-right">Amount</th><th className="px-4 py-3 text-left">Description</th><th className="px-4 py-3 text-left">Date</th><th className="px-4 py-3 text-center">Receipt</th><th className="px-4 py-3 text-center">Status</th><th className="px-4 py-3 text-center">Actions</th></tr></thead>
          <tbody>
            {claims.length === 0 ? <tr><td colSpan={7} className="text-center py-8 text-[#64748B]">No claims</td></tr> :
              claims.map(c => (
                <tr key={c.id} className="border-t border-[#E2E8F0]">
                  <td className="px-4 py-3"><span className="px-2 py-0.5 bg-[#F8FAFC] rounded text-xs">{c.category}</span></td>
                  <td className="px-4 py-3 text-right font-semibold">{fmt(c.amount)}</td>
                  <td className="px-4 py-3 text-[#64748B] max-w-[200px] truncate">{c.description || '—'}</td>
                  <td className="px-4 py-3">{c.receipt_date || c.created_at?.split('T')[0]}</td>
                  <td className="px-4 py-3 text-center">{c.has_receipt ? <button onClick={() => downloadReceipt(c.id, c.receipt_filename)} className="text-xs text-blue-600 underline">{c.receipt_filename}</button> : '—'}</td>
                  <td className="px-4 py-3 text-center"><span className={`px-2 py-0.5 rounded text-xs font-medium ${c.status === 'approved' ? 'bg-green-100 text-green-700' : c.status === 'paid' ? 'bg-blue-100 text-blue-700' : c.status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{c.status}</span></td>
                  <td className="px-4 py-3 text-center">{c.status === 'pending' && (
                    <div className="flex justify-center gap-1">
                      <Button variant="ghost" size="sm" onClick={() => openEdit(c)} className="h-7 text-blue-600"><PencilSimple size={14} /></Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(c.id)} className="h-7 text-red-500"><Trash size={14} /></Button>
                    </div>
                  )}</td>
                </tr>
              ))
            }
          </tbody>
        </table>
      </div>

      {/* New Claim Dialog */}
      <Dialog open={addDialog} onOpenChange={setAddDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
          <DialogHeader><DialogTitle>New Reimbursement Claim</DialogTitle></DialogHeader>
          {claimForm(false)}
          <DialogFooter><Button onClick={() => submitClaim(false)} disabled={!form.category || !form.amount} data-testid="emp-submit-claim-btn">Submit</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Claim Dialog */}
      <Dialog open={editDialog} onOpenChange={setEditDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-md">
          <DialogHeader><DialogTitle>Edit Claim</DialogTitle></DialogHeader>
          {claimForm(true)}
          <DialogFooter><Button onClick={() => submitClaim(true)} disabled={!form.category || !form.amount}>Update</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// ASSETS TAB
// ═══════════════════════════════════════════════════════════════════════════════
function AssetsTab() {
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => { employeeAPI.getAssets().then(res => setAssets(res.data.assets || [])).catch(() => toast.error('Failed')).finally(() => setLoading(false)); }, []);
  if (loading) return <div className="text-center py-12 text-[#64748B]">Loading...</div>;
  return (
    <div className="space-y-3" data-testid="emp-assets-tab">
      {assets.length === 0 ? <div className="text-center py-12 text-[#64748B] bg-white border border-[#E2E8F0] rounded-xl text-sm">No assets allocated</div> :
        assets.map(a => (
          <div key={a.id} className="bg-white border border-[#E2E8F0] rounded-xl p-3 sm:p-4 flex items-center justify-between gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-10 h-10 rounded-full bg-[#F8FAFC] flex items-center justify-center flex-shrink-0"><Laptop size={18} className="text-[#0F4C81]" /></div>
              <div className="min-w-0"><p className="font-medium text-sm truncate">{a.asset_name}</p><p className="text-xs text-[#64748B] truncate">{a.asset_type} | {a.make_model || 'N/A'}</p></div>
            </div>
            <div className="text-right flex-shrink-0"><span className={`px-2 py-0.5 rounded text-xs font-medium ${a.status === 'issued' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{a.status}</span>{a.issued_date && <p className="text-[10px] text-[#64748B] mt-1">{a.issued_date}</p>}</div>
          </div>
        ))
      }
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// TAX TAB
// ═══════════════════════════════════════════════════════════════════════════════
function TaxTab() {
  const [taxData, setTaxData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form16, setForm16] = useState([]);
  useEffect(() => { employeeAPI.getTax().then(res => setTaxData(res.data)).catch(() => {}).finally(() => setLoading(false)); }, []);
  useEffect(() => { employeeAPI.getForm16().then(res => setForm16(res.data.documents || [])).catch(() => {}); }, []);
  if (loading) return <div className="text-center py-12 text-[#64748B]">Loading...</div>;
  if (!taxData) return <div className="text-center py-12 text-[#64748B] bg-white border border-[#E2E8F0] rounded-xl text-sm">Tax data not available</div>;
  const comp = taxData.computation || {};
  return (
    <div className="space-y-4" data-testid="emp-tax-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-3 sm:p-4"><p className="text-[10px] sm:text-xs text-[#64748B]">Gross Income (YTD)</p><p className="text-lg sm:text-2xl font-bold">{fmt(comp.gross_annual)}</p></div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-3 sm:p-4"><p className="text-[10px] sm:text-xs text-[#64748B]">Deductions</p><p className="text-lg sm:text-2xl font-bold text-green-700">{fmt(comp.total_deductions)}</p></div>
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-3 sm:p-4"><p className="text-[10px] sm:text-xs text-[#64748B]">Taxable Income</p><p className="text-lg sm:text-2xl font-bold text-amber-700">{fmt(comp.taxable_income)}</p></div>
        <div className="bg-red-50 border border-red-200 rounded-xl p-3 sm:p-4"><p className="text-[10px] sm:text-xs text-red-600">Tax Paid (YTD)</p><p className="text-lg sm:text-2xl font-bold text-red-700">{fmt(comp.tax_paid_ytd)}</p></div>
      </div>
      {taxData.declaration && (
        <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
          <h3 className="font-semibold text-sm mb-3">Tax Declaration ({taxData.declaration.regime === 'old' ? 'Old Regime' : 'New Regime'})</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
            {[['80C', taxData.declaration.section_80c], ['80D (Medical)', taxData.declaration.section_80d], ['HRA Exemption', taxData.declaration.hra_exemption], ['LTA', taxData.declaration.lta_exemption], ['NPS (80CCD)', taxData.declaration.nps_80ccd], ['Home Loan', taxData.declaration.home_loan_interest]].map(([l, v]) => (
              v > 0 && <div key={l} className="bg-[#F8FAFC] rounded p-2"><p className="text-xs text-[#64748B]">{l}</p><p className="font-medium">{fmt(v)}</p></div>
            ))}
          </div>
        </div>
      )}
      {/* Form 16 Downloads */}
      <div className="bg-white border border-[#E2E8F0] rounded-xl p-4" data-testid="emp-form16-section">
        <div className="flex items-center gap-2 mb-3"><FileText size={18} className="text-[#0F4C81]" /><h3 className="font-semibold text-sm">Form 16</h3></div>
        {form16.length === 0 ? (
          <p className="text-sm text-[#64748B]">No Form 16 uploaded yet. Your HR team will publish it here after the financial year closes.</p>
        ) : (
          <div className="space-y-2">
            {form16.map(f => (
              <div key={f.id} className="flex items-center justify-between bg-[#F8FAFC] rounded-lg px-3 py-2" data-testid={`form16-row-${f.fiscal_year}`}>
                <div><p className="text-sm font-medium text-[#0F172A]">FY {f.fiscal_year}</p><p className="text-[11px] text-[#64748B]">{f.filename}</p></div>
                <Button size="sm" variant="outline" onClick={() => employeeAPI.downloadForm16(f.id, f.fiscal_year).catch(() => toast.error('Download failed'))} data-testid={`form16-download-${f.fiscal_year}`}><DownloadSimple size={14} className="mr-1" /> Download</Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
// ═══════════════════════════════════════════════════════════════════════════════
function HomeTab({ employee }) {
  const [announcements, setAnnouncements] = useState([]);
  const [celebrations, setCelebrations] = useState({ birthdays: [], anniversaries: [], new_joinees: [] });
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    Promise.all([
      socialAPI.getAnnouncements().catch(() => ({ data: { announcements: [] } })),
      socialAPI.getCelebrations().catch(() => ({ data: { birthdays: [], anniversaries: [], new_joinees: [] } })),
      socialAPI.getGallery().catch(() => ({ data: { photos: [] } })),
    ]).then(([aRes, cRes, gRes]) => {
      setAnnouncements(aRes.data.announcements || []);
      setCelebrations(cRes.data || { birthdays: [], anniversaries: [], new_joinees: [] });
      setGallery(gRes.data.photos || []);
    }).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-16 text-gray-400">Loading...</div>;

  const hasCelebrations = celebrations.birthdays.length > 0 || celebrations.anniversaries.length > 0 || (celebrations.new_joinees || []).length > 0;

  return (
    <div className="space-y-6" data-testid="emp-home-tab" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
      {/* Welcome Banner */}
      <div className="bg-[#0B1B3D] text-white rounded-2xl p-5 sm:p-7 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{ background: 'radial-gradient(circle at 80% 20%, #0F4C81, transparent 60%)' }} />
        <div className="relative z-10">
          <p className="text-white/60 text-xs font-semibold uppercase tracking-wider mb-1">{new Date().toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })}</p>
          <h2 className="text-xl sm:text-2xl font-bold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
            Welcome back, {employee?.display_name?.split(' ')[0] || 'there'}
          </h2>
          <p className="text-white/50 text-sm mt-1">{employee?.job_title} — {employee?.department}</p>
        </div>
      </div>

      {/* Announcements */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Megaphone size={18} weight="fill" className="text-[#0B1B3D]" />
          <h3 className="font-semibold text-[#0B1B3D]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Announcements</h3>
        </div>
        {announcements.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-6 text-center text-sm text-gray-400" data-testid="no-announcements">
            No announcements yet
          </div>
        ) : (
          <div className="space-y-3">
            {announcements.map(a => (
              <div key={a.id} className={`bg-white border rounded-2xl p-4 sm:p-5 transition-all hover:shadow-sm ${a.is_pinned ? 'border-[#0F4C81]/30 bg-orange-50/30' : 'border-gray-100'}`} data-testid={`announcement-${a.id}`}>
                <div className="flex items-start gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${a.is_pinned ? 'bg-[#0F4C81]/10 text-[#0F4C81]' : 'bg-[#0B1B3D]/5 text-[#0B1B3D]'}`}>
                    <Megaphone size={16} weight="fill" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-semibold text-[#0B1B3D] text-sm">{a.title}</h4>
                      {a.is_pinned && <span className="text-[10px] font-bold uppercase tracking-wider text-[#0F4C81] bg-[#0F4C81]/10 px-1.5 py-0.5 rounded">Pinned</span>}
                    </div>
                    <p className="text-sm text-gray-600 mt-1 whitespace-pre-line">{a.body}</p>
                    <p className="text-[10px] text-gray-400 mt-2">{new Date(a.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Celebrations — Birthdays & Anniversaries */}
      {hasCelebrations && (
        <div>
          <div className="flex items-center gap-2 mb-3">
            <Gift size={18} weight="fill" className="text-[#0F4C81]" />
            <h3 className="font-semibold text-[#0B1B3D]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Celebrations</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {celebrations.birthdays.length > 0 && (
              <div className="bg-gradient-to-br from-pink-50 to-white border border-pink-100 rounded-2xl p-4" data-testid="birthdays-section">
                <div className="flex items-center gap-2 mb-3">
                  <Cake size={16} weight="fill" className="text-pink-500" />
                  <span className="text-xs font-bold uppercase tracking-wider text-pink-500">Birthdays</span>
                </div>
                <div className="space-y-2">
                  {celebrations.birthdays.slice(0, 5).map(b => (
                    <div key={b.employee_number} className="flex items-center gap-3" data-testid={`birthday-${b.employee_number}`}>
                      <div className="w-8 h-8 rounded-full bg-pink-100 flex items-center justify-center text-sm font-bold text-pink-600">{b.name[0]}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0B1B3D] truncate">{b.name}</p>
                        <p className="text-[10px] text-gray-400">{b.department}</p>
                      </div>
                      <span className={`text-xs font-semibold ${b.is_today ? 'text-[#0F4C81]' : 'text-gray-400'}`}>
                        {b.is_today ? 'Today!' : `${b.date_display}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {celebrations.anniversaries.length > 0 && (
              <div className="bg-gradient-to-br from-amber-50 to-white border border-amber-100 rounded-2xl p-4" data-testid="anniversaries-section">
                <div className="flex items-center gap-2 mb-3">
                  <Star size={16} weight="fill" className="text-amber-500" />
                  <span className="text-xs font-bold uppercase tracking-wider text-amber-500">Work Anniversaries</span>
                </div>
                <div className="space-y-2">
                  {celebrations.anniversaries.slice(0, 5).map(a => (
                    <div key={a.employee_number} className="flex items-center gap-3" data-testid={`anniversary-${a.employee_number}`}>
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center text-sm font-bold text-amber-600">{a.years}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0B1B3D] truncate">{a.name}</p>
                        <p className="text-[10px] text-gray-400">{a.years} year{a.years > 1 ? 's' : ''}</p>
                      </div>
                      <span className={`text-xs font-semibold ${a.is_today ? 'text-[#0F4C81]' : 'text-gray-400'}`}>
                        {a.is_today ? 'Today!' : `${a.date_display}`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(celebrations.new_joinees || []).length > 0 && (
              <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 rounded-2xl p-4" data-testid="new-joinees-section">
                <div className="flex items-center gap-2 mb-3">
                  <UsersThree size={16} weight="fill" className="text-emerald-600" />
                  <span className="text-xs font-bold uppercase tracking-wider text-emerald-600">New Joinees</span>
                </div>
                <div className="space-y-2">
                  {celebrations.new_joinees.slice(0, 5).map(j => (
                    <div key={j.employee_number} className="flex items-center gap-3" data-testid={`new-joinee-${j.employee_number}`}>
                      <div className="w-8 h-8 rounded-full bg-emerald-100 flex items-center justify-center text-sm font-bold text-emerald-600">{j.name[0]}</div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[#0B1B3D] truncate">{j.name}</p>
                        <p className="text-[10px] text-gray-400">{j.job_title || j.department}</p>
                      </div>
                      <span className="text-xs font-semibold text-gray-400">
                        {j.days_ago === 0 ? 'Today!' : j.days_ago === 1 ? 'Yesterday' : `${j.days_ago}d ago`}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Photo Gallery */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <ImageIcon size={18} weight="fill" className="text-[#0B1B3D]" />
          <h3 className="font-semibold text-[#0B1B3D]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Life at HearClear</h3>
        </div>
        {gallery.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-6 text-center text-sm text-gray-400" data-testid="no-gallery">
            No photos yet
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" data-testid="gallery-grid">
            {gallery.map(photo => (
              <button
                key={photo.id}
                onClick={() => setLightbox(photo)}
                className="aspect-square rounded-2xl overflow-hidden bg-gray-100 border border-gray-100 hover:shadow-md transition-all group"
                data-testid={`gallery-photo-${photo.id}`}
              >
                <img src={`${API_URL}${photo.url}`} alt={photo.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox */}
      <Dialog open={!!lightbox} onOpenChange={() => setLightbox(null)}>
        <DialogContent className="max-w-2xl p-0 overflow-hidden rounded-2xl" data-testid="gallery-lightbox">
          {lightbox && (
            <>
              <img src={`${API_URL}${lightbox.url}`} alt={lightbox.title} className="w-full max-h-[70vh] object-contain bg-black" />
              {(lightbox.title || lightbox.description) && (
                <div className="p-4">
                  {lightbox.title && <h4 className="font-semibold text-[#0B1B3D]">{lightbox.title}</h4>}
                  {lightbox.description && <p className="text-sm text-gray-500 mt-1">{lightbox.description}</p>}
                  <p className="text-[10px] text-gray-400 mt-2">{new Date(lightbox.created_at).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}</p>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// SALARY HISTORY / APPRAISAL TIMELINE
// ═══════════════════════════════════════════════════════════════════════════════
function SalaryHistoryTab() {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    employeeAPI.getSalaryHistory()
      .then(res => setHistory(res.data.history || []))
      .catch(() => toast.error('Failed to load salary history'))
      .finally(() => setLoading(false));
  }, []);

  const fmtINR = (v) => v ? `INR ${Number(v).toLocaleString('en-IN')}` : 'INR 0';
  const fmtDate = (d) => {
    if (!d) return '';
    const dt = new Date(d);
    return dt.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  if (loading) return <div className="text-center py-12 text-[#64748B]">Loading salary history...</div>;

  if (!history.length) return (
    <div className="text-center py-12">
      <TrendUp size={48} className="mx-auto text-gray-300 mb-3" />
      <p className="text-[#64748B]">No salary history available</p>
    </div>
  );

  return (
    <div className="space-y-4" data-testid="salary-history-tab">
      <div className="flex items-center gap-2 mb-2">
        <TrendUp size={20} className="text-[#0B1B3D]" />
        <h2 className="text-lg font-semibold" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Salary Timeline</h2>
      </div>

      <div className="relative">
        {/* Vertical timeline line */}
        <div className="absolute left-5 top-8 bottom-8 w-0.5 bg-gradient-to-b from-[#6366f1] via-[#8b5cf6] to-[#d4d4d8]" />

        <div className="space-y-6">
          {history.map((record, i) => (
            <div key={`${record.effective_date || record.financial_year || 'entry'}-${i}`} className="relative pl-14" data-testid={`salary-revision-${i}`}>
              {/* Timeline dot */}
              <div className={`absolute left-3 w-5 h-5 rounded-full border-2 flex items-center justify-center ${i === 0 ? 'bg-[#6366f1] border-[#6366f1]' : 'bg-white border-[#8b5cf6]'}`}>
                <TrendUp size={10} className={i === 0 ? 'text-white' : 'text-[#8b5cf6]'} />
              </div>

              {/* Card */}
              <div className={`rounded-xl border ${i === 0 ? 'border-[#6366f1]/30 bg-white shadow-sm' : 'border-gray-200 bg-white'}`}>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="font-semibold text-[#0F172A]" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Salary Revision</span>
                    <span className="text-xs text-[#64748B]">Effective {fmtDate(record.effective_date)}</span>
                    {i === 0 && <span className="text-[10px] bg-[#6366f1] text-white px-2 py-0.5 rounded-full font-semibold uppercase tracking-wider">Current</span>}
                    {record.pct_change_ctc !== undefined && record.pct_change_ctc !== 0 && (
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-semibold ${record.pct_change_ctc > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                        {record.pct_change_ctc > 0 ? '+' : ''}{record.pct_change_ctc}%
                      </span>
                    )}
                  </div>

                  <div className="flex items-center gap-3 flex-wrap">
                    <button onClick={() => setExpanded(expanded === i ? null : i)} className="text-gray-400 hover:text-gray-600 transition-colors">
                      <CaretDown size={16} className={`transition-transform ${expanded === i ? 'rotate-180' : ''}`} />
                    </button>
                    <div className="bg-[#F8FAFC] rounded-lg px-4 py-2 text-center min-w-[140px]">
                      <p className="text-[10px] text-[#64748B] uppercase tracking-wider font-semibold">Regular Salary</p>
                      <p className="text-base font-bold text-[#0F172A]">{fmtINR(record.revised_regular_salary || record.regular_salary)}</p>
                    </div>
                    <span className="text-lg text-[#64748B]">→</span>
                    <div className="bg-[#F8FAFC] rounded-lg px-4 py-2 text-center min-w-[140px]">
                      <p className="text-[10px] text-[#64748B] uppercase tracking-wider font-semibold">CTC (Annual)</p>
                      <p className="text-base font-bold text-[#0F4C81]">{fmtINR(record.revised_ctc || record.total_ctc)}</p>
                    </div>
                    {record.previous_ctc > 0 && (
                      <div className="bg-gray-50 rounded-lg px-3 py-2 text-center min-w-[120px]">
                        <p className="text-[10px] text-[#64748B] uppercase tracking-wider font-semibold">Previous CTC</p>
                        <p className="text-sm text-[#64748B]">{fmtINR(record.previous_ctc)}</p>
                      </div>
                    )}
                    <button onClick={() => setExpanded(expanded === i ? null : i)} className="ml-auto text-xs text-[#6366f1] hover:underline" data-testid={`view-breakup-${i}`}>
                      View Salary breakup
                    </button>
                  </div>

                  {/* Expanded Breakup */}
                  {expanded === i && (
                    <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4" data-testid={`breakup-${i}`}>
                      <div>
                        <p className="text-xs font-semibold text-green-700 uppercase tracking-wider mb-2">Earnings</p>
                        <div className="space-y-1">
                          {Object.entries(record.earnings || {}).map(([k, v]) => (
                            <div key={k} className="flex justify-between text-sm">
                              <span className="text-[#64748B]">{k}</span>
                              <span className="font-medium">{fmtINR(v)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between text-sm font-bold border-t pt-1 mt-1">
                            <span>Total Earnings</span>
                            <span className="text-green-700">{fmtINR(record.total_ctc)}</span>
                          </div>
                        </div>
                      </div>
                      <div>
                        <p className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-2">Deductions</p>
                        <div className="space-y-1">
                          {Object.entries(record.deductions || {}).map(([k, v]) => (
                            <div key={k} className="flex justify-between text-sm">
                              <span className="text-[#64748B]">{k}</span>
                              <span className="font-medium text-red-600">{fmtINR(v)}</span>
                            </div>
                          ))}
                          <div className="flex justify-between text-sm font-bold border-t pt-1 mt-1">
                            <span>Total Deductions</span>
                            <span className="text-red-600">{fmtINR(record.total_deductions)}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// APPROVALS TAB (Manager) — approve team leaves & reimbursement claims
// ═══════════════════════════════════════════════════════════════════════════════
function ApprovalsTab() {
  const [leaves, setLeaves] = useState([]);
  const [claims, setClaims] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lRes, cRes] = await Promise.all([
        employeeAPI.getApprovalLeaves(),
        employeeAPI.getApprovalReimbursements(),
      ]);
      setLeaves(lRes.data.requests || []);
      setClaims(cRes.data.claims || []);
    } catch { toast.error('Failed to load approvals'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const actLeave = async (id, approve) => {
    try {
      if (approve) await employeeAPI.approveLeaveRequest(id);
      else await employeeAPI.rejectLeaveRequest(id, '');
      toast.success(approve ? 'Leave approved — forwarded to HR' : 'Leave rejected');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const actClaim = async (id, approve) => {
    try {
      if (approve) await employeeAPI.approveReimbursementClaim(id);
      else await employeeAPI.rejectReimbursementClaim(id, '');
      toast.success(approve ? 'Claim approved — forwarded to HR' : 'Claim rejected');
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const downloadReceipt = async (claimId, filename) => {
    try {
      const token = localStorage.getItem('emp_token');
      const res = await fetch(`${API_URL}/api/employee/reimbursement/${claimId}/receipt`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error();
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a'); a.href = url; a.download = filename || 'receipt'; a.click(); URL.revokeObjectURL(url);
    } catch { toast.error('Download failed'); }
  };

  if (loading) return <div className="text-center py-12 text-[#64748B]">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="emp-approvals-tab">
      <p className="text-sm text-[#64748B]">As a reporting manager, approve your team's requests here. Approved items are forwarded to HR for final processing.</p>

      {/* Leaves */}
      <div>
        <h3 className="font-semibold text-sm text-[#0F4C81] mb-2">Leave Requests ({leaves.length})</h3>
        {leaves.length === 0 ? (
          <div className="text-center py-6 text-[#64748B] bg-white border border-[#E2E8F0] rounded-xl text-sm">No pending leave approvals</div>
        ) : (
          <div className="space-y-2">
            {leaves.map(r => (
              <div key={r.id} className="bg-white border border-[#E2E8F0] rounded-xl p-3 flex items-center justify-between gap-3" data-testid={`approve-leave-row-${r.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#0F172A] truncate">{r.employee_name || r.employee_number} <span className="text-[#64748B] font-normal">({r.employee_number})</span></p>
                  <p className="text-xs text-[#64748B]">{r.leave_type_code} · {r.start_date} → {r.end_date} · {r.days} day(s){r.lop_days > 0 && <span className="text-amber-600 font-medium"> · LOP {r.lop_days}d</span>}</p>
                  <p className="text-xs text-[#64748B] truncate">{r.reason || 'No reason'}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" variant="outline" className="text-green-600 border-green-200 h-8" onClick={() => actLeave(r.id, true)} data-testid={`approve-leave-btn-${r.id}`}><Check size={14} className="mr-1" />Approve</Button>
                  <Button size="sm" variant="outline" className="text-red-500 border-red-200 h-8" onClick={() => actLeave(r.id, false)} data-testid={`reject-leave-btn-${r.id}`}><X size={14} className="mr-1" />Reject</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Reimbursements */}
      <div>
        <h3 className="font-semibold text-sm text-[#0F4C81] mb-2">Reimbursement Claims ({claims.length})</h3>
        {claims.length === 0 ? (
          <div className="text-center py-6 text-[#64748B] bg-white border border-[#E2E8F0] rounded-xl text-sm">No pending claim approvals</div>
        ) : (
          <div className="space-y-2">
            {claims.map(c => (
              <div key={c.id} className="bg-white border border-[#E2E8F0] rounded-xl p-3 flex items-center justify-between gap-3" data-testid={`approve-claim-row-${c.id}`}>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-[#0F172A] truncate">{c.employee_name || c.employee_number} <span className="text-[#64748B] font-normal">({c.employee_number})</span></p>
                  <p className="text-xs text-[#64748B]">{c.category} · <b>{fmt(c.amount)}</b> · {c.receipt_date || c.created_at?.split('T')[0]}</p>
                  <p className="text-xs text-[#64748B] truncate">{c.description || '—'}{c.has_receipt && <button onClick={() => downloadReceipt(c.id, c.receipt_filename)} className="text-blue-600 underline ml-1">receipt</button>}</p>
                </div>
                <div className="flex gap-2 flex-shrink-0">
                  <Button size="sm" variant="outline" className="text-green-600 border-green-200 h-8" onClick={() => actClaim(c.id, true)} data-testid={`approve-claim-btn-${c.id}`}><Check size={14} className="mr-1" />Approve</Button>
                  <Button size="sm" variant="outline" className="text-red-500 border-red-200 h-8" onClick={() => actClaim(c.id, false)} data-testid={`reject-claim-btn-${c.id}`}><X size={14} className="mr-1" />Reject</Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════════════════
// MAIN EMPLOYEE PORTAL
// ═══════════════════════════════════════════════════════════════════════════════
const TABS = [
  { key: 'home', label: 'Home', icon: House },
  { key: 'attendance', label: 'Attendance', icon: Clock },
  { key: 'salary', label: 'Salary', icon: Money },
  { key: 'appraisals', label: 'Appraisals', icon: TrendUp },
  { key: 'leave', label: 'Leave', icon: CalendarBlank },
  { key: 'reimbursements', label: 'Claims', icon: Receipt },
  { key: 'profile', label: 'Profile', icon: User },
  { key: 'tax', label: 'Tax', icon: FileText },
  { key: 'assets', label: 'Assets', icon: Laptop },
];

export default function EmployeePortalPage() {
  const [employee, setEmployee] = useState(null);
  const [token, setToken] = useState(null);
  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('home');
  const [changePwDialog, setChangePwDialog] = useState(false);
  const [pwForm, setPwForm] = useState({ current_password: '', new_password: '', confirm: '' });
  const [approvals, setApprovals] = useState({ is_manager: false, leaves: 0, reimbursements: 0 });

  useEffect(() => {
    const savedToken = localStorage.getItem('emp_token');
    const savedData = localStorage.getItem('emp_data');
    if (savedToken && savedData) {
      try { setToken(savedToken); setEmployee(JSON.parse(savedData)); } catch { localStorage.removeItem('emp_token'); localStorage.removeItem('emp_data'); }
    }
  }, []);

  const loadProfile = useCallback(() => {
    if (!token) return;
    employeeAPI.getProfile()
      .then(res => setProfile(res.data))
      .catch(err => { if (err.response?.status === 401) handleLogout(); });
  }, [token]);

  useEffect(() => { loadProfile(); }, [loadProfile]);

  useEffect(() => {
    if (!token) return;
    employeeAPI.getApprovalsPending()
      .then(res => setApprovals(res.data || { is_manager: false }))
      .catch(() => {});
  }, [token, activeTab]);

  const isManager = approvals.is_manager;
  const pendingCount = (approvals.leaves || 0) + (approvals.reimbursements || 0);
  const visibleTabs = isManager
    ? [...TABS.slice(0, 6), { key: 'approvals', label: 'Approvals', icon: UsersThree }, ...TABS.slice(6)]
    : TABS;

  const handleLogin = (emp, tok) => { setEmployee(emp); setToken(tok); };
  const handleLogout = () => { localStorage.removeItem('emp_token'); localStorage.removeItem('emp_data'); setEmployee(null); setToken(null); setProfile(null); };

  const handleChangePw = async () => {
    if (pwForm.new_password !== pwForm.confirm) { toast.error('Passwords do not match'); return; }
    try {
      await employeeAPI.changePassword({ current_password: pwForm.current_password, new_password: pwForm.new_password });
      toast.success('Password changed'); setChangePwDialog(false); setPwForm({ current_password: '', new_password: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (!employee || !token) return <EmployeeLogin onLogin={handleLogin} />;

  return (
    <div className="min-h-screen pb-20 sm:pb-0" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif', background: '#F8F9FB' }} data-testid="employee-portal">
      {/* Header */}
      <header className="bg-[#0B1B3D] text-white px-4 py-3 flex items-center justify-between sticky top-0 z-30 shadow-lg">
        <div className="flex items-center gap-3">
          <img src="/hearclear_logo.png" alt="HearClear" className="h-7 brightness-0 invert" data-testid="header-logo" />
          <div className="h-5 w-px bg-white/20 hidden sm:block" />
          <span className="text-[10px] text-white/40 hidden sm:block uppercase tracking-wider" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Employee Portal</span>
        </div>
        <div className="flex items-center gap-1 sm:gap-3">
          <div className="text-right hidden sm:block">
            <p className="text-sm font-semibold">{employee.display_name}</p>
            <p className="text-[10px] text-white/50">{employee.employee_number} — {employee.department}</p>
          </div>
          <div className="sm:hidden text-right mr-1">
            <p className="text-xs font-semibold truncate max-w-[120px]">{employee.display_name}</p>
            <p className="text-[10px] text-white/50">{employee.employee_number}</p>
          </div>
          <button onClick={() => setChangePwDialog(true)} className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-xl transition-all" data-testid="emp-change-pw-btn"><Lock size={16} /></button>
          <button onClick={handleLogout} className="p-2 text-white/40 hover:text-white hover:bg-white/10 rounded-xl transition-all" data-testid="emp-logout-btn"><SignOut size={16} /></button>
        </div>
      </header>

      {/* Desktop Tab Bar */}
      <div className="hidden sm:block sticky top-[52px] z-20 bg-white/80 backdrop-blur-xl border-b border-gray-200/50 px-4">
        <div className="max-w-[1200px] mx-auto flex gap-1 overflow-x-auto py-1.5">
          {visibleTabs.map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setActiveTab(key)} data-testid={`emp-tab-${key}`}
              className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-xl whitespace-nowrap transition-all duration-200 ${activeTab === key ? 'bg-[#0B1B3D] text-white font-semibold shadow-sm' : 'text-gray-500 hover:bg-gray-100 hover:text-[#0B1B3D]'}`}
              style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>
              <Icon size={15} weight={activeTab === key ? 'fill' : 'regular'} /> {label}
              {key === 'approvals' && pendingCount > 0 && <span className="ml-1 bg-red-500 text-white text-[10px] font-bold rounded-full px-1.5 py-0.5 leading-none" data-testid="approvals-badge">{pendingCount}</span>}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-[1200px] mx-auto p-3 sm:p-6">
        {activeTab === 'home' && <HomeTab employee={employee} />}
        {activeTab === 'profile' && <ProfileTab profile={profile} token={token} onProfileUpdate={loadProfile} />}
        {activeTab === 'salary' && <SalaryTab empNumber={employee.employee_number} />}
        {activeTab === 'appraisals' && <SalaryHistoryTab />}
        {activeTab === 'leave' && <LeaveTab />}
        {activeTab === 'attendance' && <AttendanceTab />}
        {activeTab === 'reimbursements' && <ReimbursementTab />}
        {activeTab === 'tax' && <TaxTab />}
        {activeTab === 'assets' && <AssetsTab />}
        {activeTab === 'approvals' && <ApprovalsTab />}

        {/* Professional Footer */}
        <footer className="py-6 mt-8 border-t border-gray-200" data-testid="emp-footer">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <img src="/hearclear_logo.png" alt="HearClear" className="h-5 opacity-60" />
              <span className="text-xs text-gray-400">|</span>
              <span className="text-[10px] text-gray-400 uppercase tracking-wider" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Employee Self-Service</span>
            </div>
            <p className="text-[10px] text-gray-400">© {new Date().getFullYear()} HearClear Hearing Clinics Pvt. Ltd. All rights reserved. Powered by Fintexa.</p>
          </div>
        </footer>
      </div>

      {/* Mobile Bottom Navigation — Glassmorphism */}
      <nav className="sm:hidden fixed bottom-0 left-0 right-0 z-30 px-2 py-1.5 safe-area-bottom" style={{ background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(20px)', WebkitBackdropFilter: 'blur(20px)', borderTop: '1px solid rgba(229,231,235,0.5)' }} data-testid="emp-bottom-nav">
        <div className="flex justify-around">
          {TABS.slice(0, 5).map(({ key, label, icon: Icon }) => (
            <button key={key} onClick={() => setActiveTab(key)} data-testid={`emp-mob-tab-${key}`}
              className={`flex flex-col items-center py-1.5 px-2 rounded-xl min-w-0 flex-1 transition-all duration-200 ${activeTab === key ? 'text-[#0B1B3D]' : 'text-gray-400'}`}>
              <Icon size={20} weight={activeTab === key ? 'fill' : 'regular'} />
              <span className={`text-[9px] mt-0.5 truncate ${activeTab === key ? 'font-bold' : ''}`} style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{label}</span>
            </button>
          ))}
          <button onClick={() => {
            const moreKeys = isManager ? ['profile', 'tax', 'assets', 'approvals'] : ['profile', 'tax', 'assets'];
            const idx = moreKeys.indexOf(activeTab);
            setActiveTab(moreKeys[(idx + 1) % moreKeys.length]);
          }} data-testid="emp-mob-tab-more"
            className={`relative flex flex-col items-center py-1.5 px-2 rounded-xl min-w-0 flex-1 transition-all duration-200 ${['profile', 'tax', 'assets', 'approvals'].includes(activeTab) ? 'text-[#0B1B3D]' : 'text-gray-400'}`}>
            <CaretRight size={20} /><span className="text-[9px] mt-0.5" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>More</span>
            {isManager && pendingCount > 0 && <span className="absolute top-0 right-2 bg-red-500 text-white text-[9px] font-bold rounded-full px-1 leading-none">{pendingCount}</span>}
          </button>
        </div>
      </nav>

      {/* Change Password Dialog */}
      <Dialog open={changePwDialog} onOpenChange={setChangePwDialog}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-sm rounded-2xl">
          <DialogHeader><DialogTitle style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>Change Password</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input type="password" placeholder="Current password" className="rounded-xl" value={pwForm.current_password} onChange={(e) => setPwForm(f => ({...f, current_password: e.target.value}))} />
            <Input type="password" placeholder="New password" className="rounded-xl" value={pwForm.new_password} onChange={(e) => setPwForm(f => ({...f, new_password: e.target.value}))} />
            <Input type="password" placeholder="Confirm new password" className="rounded-xl" value={pwForm.confirm} onChange={(e) => setPwForm(f => ({...f, confirm: e.target.value}))} />
          </div>
          <DialogFooter><Button onClick={handleChangePw} disabled={!pwForm.current_password || !pwForm.new_password || !pwForm.confirm} className="bg-[#0B1B3D] hover:bg-[#16274D] rounded-xl">Change Password</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
